import { useState } from "react";
import { useHistory } from "react-router-dom";
import { ShowLess, ShowMore, EditPenIcon } from "../icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { getBookingPriceBreakdown } from "../../utils/discount";

const BookingCard = ({
    bookingDetail,
    source = "my-booking",
    actions = null,
}) => {
    const { t, i18n } = useTranslation();
    // Same figures as the price card: never read `totalPriceBeforeDiscount` directly, the
    // backend leaves a stale one behind once a discount is removed.
    const priceBreakdown = getBookingPriceBreakdown(bookingDetail);
    const [showMore, setShowMore] = useState(source === "cart");
    const [isHoveringMore, setIsHoveringMore] = useState(false);
    const [isHoveringLess, setIsHoveringLess] = useState(false);
    const history = useHistory();

    if (i18n.language === "en") {
        moment.locale("en");
    } else {
        moment.locale("ja");
    }

    const formatDate = {
        "en": "MMM. Do, YYYY (ddd) HH:mm",
        "jp": "YYYY年M月D日(dd) HH:mm"
    }

    const handleCardClick = () => {
        history.push(`/booking-detail/${bookingDetail._id}`);
    };

    const handleShowMoreClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowMore(true);
    };

    const handleShowLessClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowMore(false);
    };

    const getStatusBooking = () => {
        if (bookingDetail.bookingStatus === "CANCELLED") {
            return {
                bgColor: "#FFEAE7",
                textColor: "#EA4335",
                text: t("myPage.booking.bookingItem.status.cancelled"),
                hasRequest: false
            };
        } else {
            const currentUTC = Date.now();
            const currentJST = new Date(currentUTC + 9 * 60 * 60 * 1000);

            // Parse endTime (garageArivalTime is UTC-based ISO)
            const endTime = new Date(bookingDetail.garageArivalTime);

            if (currentJST > endTime) {
                return {
                    bgColor: "#D8ECD1",
                    textColor: "#3C7120",
                    text: t("myPage.booking.bookingItem.status.completed"),
                    hasRequest: false
                }
            }

            if (bookingDetail.paymentStatus === "CHARGE_PENDING") {
                let hasRequest = false;
                if (
                    bookingDetail?.requestChangeBooking?.some(item => item.status === "PENDING_APPROVAL") ||
                    (bookingDetail?.requestChangeBooking?.[0]?.status === "REJECTED" && bookingDetail?.requestChangeBooking?.[0]?.showRejection)
                ) {
                    hasRequest = true;
                }
                return {
                    bgColor: "#F6BB2A",
                    textColor: "#FFFFFF",
                    text: t("myPage.booking.bookingItem.status.pendingConfirmation"),
                    hasRequest: hasRequest
                }
            } else if (bookingDetail.paymentStatus === "CHARGED" || bookingDetail.paymentStatus === "ADDED_TO_MONTHLY_TOTAL") {
                let hasRequest = false;
                if (
                    bookingDetail?.requestChangeBooking?.some(item => item.status === "PENDING_APPROVAL") ||
                    (bookingDetail?.requestChangeBooking?.[0]?.status === "REJECTED" && bookingDetail?.requestChangeBooking?.[0]?.showRejection)
                ) {
                    hasRequest = true;
                }
                return {
                    bgColor: "#3CAEA3",
                    textColor: "#FFFFFF",
                    text: t("myPage.booking.bookingItem.status.confirmed"),
                    hasRequest: hasRequest
                }
            }
        }

        return {
            bgColor: "#F6BB2A",
            textColor: "#FFFFFF",
            text: t("myPage.booking.bookingItem.status.pendingConfirmation"),
            hasRequest: false
        }
    }

    const getTransferTypeLabel = () => {
        if (bookingDetail?.bookingType === "byTheHour") {
            return t("myPage.booking.bookingItem.bookingType.byTheHour");
        } else {
            if (bookingDetail?.isAirportTransfer) {
                return t("myPage.booking.bookingItem.bookingType.airportTransfer");
            } else {
                return t("myPage.booking.bookingItem.bookingType.specifiedDestination");
            }
        }
    }

    const getLocationDetail = (loc) => {
        const formattedAddressArr = loc?.formatted_address?.split(",");
        const name = formattedAddressArr?.[0];
        const address = formattedAddressArr?.slice(1).join(",");

        return {
            name,
            address
        }
    }

    const getAddOnServices = () => {
        let addOnServices = "";

        if (bookingDetail?.meetAssist[0].meetAssistName !== "none" || bookingDetail?.meetAssist[1].meetAssistName !== "none") {
            if (bookingDetail?.meetAssist[0].meetAssistName !== "none") {
                const text = t(`myPage.booking.bookingItem.addOnService.${bookingDetail?.meetAssist[0].meetAssistName}.${bookingDetail?.isFromAirport ? "airport" : "station"}.arrival`);
                addOnServices += text;

                if (bookingDetail?.meetAssist[1].meetAssistName !== "none") addOnServices += `\n`
            }

            if (bookingDetail?.meetAssist[1].meetAssistName !== "none") {
                const text = t(`myPage.booking.bookingItem.addOnService.${bookingDetail?.meetAssist[1].meetAssistName}.${bookingDetail?.isFromAirport ? "airport" : "station"}.departure`);
                addOnServices += text;
            }

            if (bookingDetail.totalChildSeatPrice > 0) addOnServices += `\n`
        }

        if (bookingDetail.totalChildSeatPrice > 0) {
            const countInfant = bookingDetail.bookingInfo.reduce((sum, booking) => sum + booking?.childSeat[0]?.childSeatCount, 0)
            const countToddler = bookingDetail.bookingInfo.reduce((sum, booking) => sum + booking?.childSeat[1]?.childSeatCount, 0)
            const countChild = bookingDetail.bookingInfo.reduce((sum, booking) => sum + booking?.childSeat[2]?.childSeatCount, 0)

            const text = `${t(`myPage.booking.bookingItem.addOnService.childSeat`)} (${countInfant > 0 ? `${t(`myPage.booking.bookingItem.addOnService.childSeatType.infant`, { count: countInfant })}` : ""}${(countInfant > 0 && (countToddler > 0 || countChild > 0)) ? ", " : ""}${countToddler > 0 ? `${t(`myPage.booking.bookingItem.addOnService.childSeatType.toddler`, { count: countToddler })}` : ""}${(countToddler > 0 && countChild > 0) ? ", " : ""}${countChild > 0 ? `${t(`myPage.booking.bookingItem.addOnService.childSeatType.child`, { count: countChild })}` : ""})`;
            addOnServices += text;
        }

        return addOnServices;
    }

    const getAdditionalInformation = () => {
        if (!bookingDetail?.pickupFlightNo && !bookingDetail?.dropoffFlightNo) {
            return <span className="tw:!text-[#8d8d8d] tw:!italic">{t("myPage.booking.bookingItem.additionalInformation.none")}</span>;
        }

        let additionalInformation = "";

        if (bookingDetail?.pickupFlightNo) {
            additionalInformation += t(`myPage.booking.bookingItem.additionalInformation.flightNumber.${bookingDetail?.isFromAirport ? "airport" : "station"}.arrival`, {
                pickupFlightNo: bookingDetail?.pickupFlightNo,
                pickupSign: `${bookingDetail?.pickupSignHonorific || "-"} ${bookingDetail?.pickupSign}`
            })

            if (bookingDetail?.dropoffFlightNo) additionalInformation += `\n`;
        }

        if (bookingDetail?.dropoffFlightNo) {
            additionalInformation += t(`myPage.booking.bookingItem.additionalInformation.flightNumber.${bookingDetail?.isToAirport ? "airport" : "station"}.departure`, {
                dropoffFlightNo: bookingDetail?.dropoffFlightNo,
            })
        }

        return additionalInformation;
    }

    function minutesToHoursCeil(minutes) {
        return Math.ceil(minutes / 30) * 0.5;
    }

    const isEditable = () => {
        if (bookingDetail.bookingStatus === "CANCELLED") {
            return false;
        } else {
            // Get current time in JST (UTC + 9 Hours)
            const currentUTC = Date.now();
            const currentJST = new Date(currentUTC + 9 * 60 * 60 * 1000);

            // Parse endTime (garageArivalTime is UTC-based ISO)
            const endTime = new Date(bookingDetail.garageArivalTime);

            if (currentJST > endTime) {
                return false
            }
        }

        return true
    }

    const getRequestChangeStatus = () => {
        if (bookingDetail?.requestChangeBooking?.[0]?.status === "REJECTED" && bookingDetail?.requestChangeBooking?.[0]?.showRejection) {
            return {
                bgColor: "#FFEAE7",
                textColor: "#EA4335",
                text: t("myPage.booking.bookingItem.status.changeRejected")
            };
        }

        return {
            bgColor: "#20639B1A",
            textColor: "#20639B",
            text: t("myPage.booking.bookingItem.status.changeRequested")
        };
    }

    const getEligibilityEditBooking = () => {
        if (bookingDetail?.requestChangeBooking?.some(item => item.status === "PENDING_APPROVAL") && bookingDetail.user.role === "individual") {
            return false;
        }

        return true
    }

    return (
        <div className="tw:!mb-5">
            {/* Mobile Only - Status Banner outside card */}
            {
                source === "my-booking" && (
                    <>
                        <div className={`tw:md:!hidden tw:!bg-[${getStatusBooking().bgColor}] tw:!border tw:!border-[${getStatusBooking().bgColor}] tw:!rounded-tl-lg tw:!rounded-tr-lg tw:!py-2 tw:!px-4 tw:!text-center`}>
                            <p className={`tw:!text-[14px] tw:!font-[400] tw:!text-[${getStatusBooking().textColor}] tw:!m-0`}>{getStatusBooking().text}</p>
                        </div>
                        {
                            getStatusBooking().hasRequest && (
                                <div className={`tw:md:!hidden tw:!bg-[${getRequestChangeStatus().bgColor}] tw:!border tw:!border-[${getRequestChangeStatus().bgColor}] tw:!border-t-0 tw:!border-b-0 tw:!py-3 tw:!px-4 tw:!text-center`}>
                                    <p className={`tw:!text-[14px] tw:!font-[400] tw:!text-[${getRequestChangeStatus().textColor}] tw:!m-0`}>{getRequestChangeStatus().text}</p>
                                </div>
                            )
                        }
                    </>
                )
            }

            <div
                onClick={source === "my-booking" ? handleCardClick : null}
                className={`tw:!mx-auto tw:!bg-white tw:!border tw:!border-[#E8E8E8] tw:!border-t-0 tw:md:!border-t tw:md:!rounded-lg tw:!rounded-b-lg tw:!shadow-sm tw:!font-sans tw:!transition-shadow tw:!duration-200 tw:hover:!shadow-md ${source === "my-booking" ? "tw:!cursor-pointer" : ""}`}
            >
                <div className="tw:!p-4 tw:md:!p-6">
                    {/* Row 1: Booking ID - edit pen button (Mobile) */}
                    {
                        source === "my-booking" ? (
                            <>
                                <div className="tw:!flex tw:!justify-between tw:!items-start tw:md:!hidden">
                                    <div className="tw:!flex-1">
                                        <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.bookingId')}</p>
                                        <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{`#${bookingDetail?.key}`}</p>
                                    </div>
                                    {
                                        isEditable() && (
                                            <button
                                                className={`tw:!flex tw:!items-center tw:!justify-center tw:!px-2 tw:!py-2 tw:!border tw:!border-gray-300 tw:!rounded-md tw:!bg-transparent tw:!transition-all tw:!duration-200 ${!getEligibilityEditBooking() ? 'tw:!cursor-not-allowed tw:!opacity-25' : 'tw:!cursor-pointer tw:hover:!bg-gray-100 tw:hover:!border-gray-400 tw:hover:!scale-105'}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    history.push(`/booking/${bookingDetail._id}/edit`);
                                                }}
                                                disabled={!getEligibilityEditBooking()}
                                            >
                                                <EditPenIcon
                                                    color="#282828"
                                                    size={15}
                                                    className="tw:!m-[2px]"
                                                />
                                            </button>
                                        )
                                    }
                                    {actions && <div className="tw:!flex tw:!items-center tw:!justify-end">{actions}</div>}
                                </div>
                                <hr className="tw:!my-4 tw:!border-gray-100 tw:md:!hidden" />
                            </>
                        ) : (
                            <div className="tw:!flex tw:!justify-end tw:!items-start tw:md:!hidden">
                                {actions && <div className="tw:!flex tw:!items-center tw:!justify-end">{actions}</div>}
                            </div>
                        )
                    }


                    {/* Row 2: Passenger Name - Type of Transfer (Mobile) */}
                    <div className="tw:!flex tw:!justify-between tw:!items-start tw:!gap-2 tw:md:!hidden">
                        <div className="tw:!flex-1">
                            <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.passengerName')}</p>
                            <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{bookingDetail?.passengerName}</p>
                        </div>
                        <div className="tw:!flex-1">
                            <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.transferType')}</p>
                            <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{getTransferTypeLabel()}</p>
                        </div>
                    </div>

                    <hr className="tw:!my-4 tw:!border-gray-100 tw:md:!hidden" />

                    {/* Row 2A: Pick-up Time - Estimated Drop-off Time (Mobile) */}
                    <div className="tw:!flex tw:!justify-between tw:!items-start tw:!gap-2 tw:md:!hidden">
                        <div className="tw:!flex-1">
                            <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.pickupTime')}</p>
                            <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#4E4E4E] tw:!m-0">{moment(bookingDetail?.pickupDateTime).format(formatDate[i18n.language])}</p>
                        </div>
                        <div className="tw:!flex-1">
                            <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.dropoffTime')}</p>
                            <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#4E4E4E] tw:!m-0">{moment(bookingDetail?.customerDropoffDateTime).format(formatDate[i18n.language])}</p>
                        </div>
                    </div>

                    <hr className="tw:!my-4 tw:!border-gray-100 tw:md:!hidden" />

                    {/* Row 3: Pickup Location (Mobile) */}
                    <div className="tw:md:!hidden tw:!mb-6">
                        <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.pickupLocation')}</p>
                        <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{getLocationDetail(bookingDetail?.pickupLocation).name}</p>
                        <p className="tw:!text-[13px] tw:!font-[400] tw:!text-[#7C7B7B] tw:!m-0">{getLocationDetail(bookingDetail?.pickupLocation).address}</p>
                    </div>

                    {/* Row 4: Dropoff Location (Mobile) */}
                    {bookingDetail?.destinations.map((item, index) => {
                        const isSingle = bookingDetail.destinations.length === 1;
                        const isLast = index === bookingDetail.destinations.length - 1;

                        return (
                            <div
                                key={index}
                                className={`tw:md:!hidden ${!isLast ? 'tw:!mb-6' : ''}`}
                            >
                                <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">
                                    {`${!isSingle ? `${index + 1}.` : ''} ${t('myPage.booking.bookingItem.title.dropoffLocation')}`}
                                </p>

                                <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">
                                    {getLocationDetail(item).name}
                                </p>

                                <p className="tw:!text-[13px] tw:!font-[400] tw:!text-[#7C7B7B] tw:!m-0">
                                    {getLocationDetail(item).address}
                                </p>
                            </div>
                        );
                    })}

                    {
                        bookingDetail.bookingType === "byTheHour" && (
                            <>
                                <hr className="tw:!my-4 tw:!border-gray-100 tw:md:!hidden" />

                                {/* Row 5: Travel Area (Mobile) */}
                                <div className="tw:md:!hidden tw:!mb-6">
                                    <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.travelArea")}</p>
                                    <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{`${t("myPage.booking.bookingItem.travelArea." + bookingDetail.travelArea)}`}</p>
                                </div>

                                {/* Row 6: Billed & Usage Duration (Mobile) */}
                                <div className="tw:!flex tw:!justify-between tw:!items-start tw:!gap-4 tw:md:!hidden">
                                    <div className="tw:!flex-1">
                                        <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.usageDuration")}</p>
                                        <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{`${minutesToHoursCeil(bookingDetail?.usageDuration)}${t("myPage.booking.bookingItem.title.hours")}`}</p>
                                    </div>
                                    <div className="tw:!flex-1">
                                        <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.billedDuration")}</p>
                                        <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{`${minutesToHoursCeil(bookingDetail?.billedDuration)}${t("myPage.booking.bookingItem.title.hours")}`}</p>
                                    </div>
                                </div>
                            </>
                        )
                    }

                    {/* Desktop Layout - First Row */}
                    {
                        source === "my-booking" ? (
                            <div className="tw:!hidden tw:md:!flex tw:!justify-between tw:!items-start">
                                <div className="tw:!flex tw:!gap-2 tw:!flex-1">
                                    <div className="tw:!min-w-1/4">
                                        <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.bookingId")}</p>
                                        <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{`#${bookingDetail?.key}`}</p>
                                    </div>
                                    <div className="tw:!flex-1 tw:!min-w-1/8">
                                        <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.passengerName')}</p>
                                        <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{bookingDetail?.passengerName}</p>
                                    </div>
                                    <div className="tw:!flex-1 tw:!min-w-0">
                                        <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.transferType')}</p>
                                        <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0 tw:!truncate">{getTransferTypeLabel()}</p>
                                    </div>
                                    <div className="tw:!flex tw:!items-center tw:!gap-2 tw:!flex-wrap tw:!justify-end tw:!min-w-1/4">
                                        <span className={`tw:!text-[14px] tw:!font-[400] tw:!text-[${getStatusBooking().textColor}] tw:!bg-[${getStatusBooking().bgColor}] tw:!px-4 tw:!py-2 tw:!rounded-md tw:!whitespace-nowrap`}>
                                            {getStatusBooking().text}
                                        </span>
                                        {
                                            getStatusBooking().hasRequest && (
                                                <span className={`tw:!text-[14px] tw:!font-[400] tw:!text-[${getRequestChangeStatus().textColor}] tw:!bg-[${getRequestChangeStatus().bgColor}] tw:!px-4 tw:!py-2 tw:!rounded-md tw:!whitespace-nowrap`}>
                                                    {getRequestChangeStatus().text}
                                                </span>
                                            )
                                        }
                                        {
                                            isEditable() && (
                                                <button
                                                    className={`tw:!flex tw:!items-center tw:!justify-center tw:!px-2 tw:!py-2 tw:!border tw:!border-gray-300 tw:!rounded-md tw:!bg-transparent tw:!transition-all tw:!duration-200 ${!getEligibilityEditBooking() ? 'tw:!cursor-not-allowed tw:!opacity-25' : 'tw:!cursor-pointer tw:hover:!bg-gray-100 tw:hover:!border-gray-400 tw:hover:!scale-105'}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        history.push(`/booking/${bookingDetail._id}/edit`);
                                                    }}
                                                    disabled={!getEligibilityEditBooking()}
                                                >
                                                    <EditPenIcon
                                                        color="#282828"
                                                        size={15}
                                                        className="tw:!m-[2px]"
                                                    />
                                                </button>
                                            )
                                        }
                                        {/* <button
                                            className="tw:!flex tw:!items-center tw:!justify-center tw:!px-2 tw:!py-2 tw:!border tw:!border-gray-300 tw:!rounded-md tw:!bg-transparent tw:!transition-all tw:!duration-200 tw:!cursor-pointer tw:hover:!bg-gray-100 tw:hover:!border-gray-400 tw:hover:!scale-105"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log("Redirect to download booking pdf");
                                            }}
                                        >
                                            <DownloadIcon
                                                color="#282828"
                                                size={15}
                                                className="tw:!m-[2px]"
                                            />
                                        </button> */}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="tw:!hidden tw:md:!flex tw:!justify-between tw:!items-start">
                                <div className="tw:!flex tw:!gap-2 tw:!flex-1">
                                    <div className="tw:!flex-1 tw:!max-w-1/4">
                                        <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.passengerName')}</p>
                                        <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{bookingDetail?.passengerName}</p>
                                    </div>
                                    <div className="tw:!flex-1 tw:!min-w-1/8">
                                        <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.transferType')}</p>
                                        <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0 tw:!truncate">{getTransferTypeLabel()}</p>
                                    </div>
                                    <div className="tw:!flex tw:!items-center tw:!gap-2 tw:!flex-wrap tw:!justify-end tw:!min-w-1/4">
                                        {actions && (
                                            <div>
                                                {actions}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    <hr className="tw:!my-4 tw:!border-gray-100 tw:!hidden tw:md:!block" />

                    {/* Desktop - Pick-up Section (Hidden on Mobile) */}
                    <div className="tw:!hidden tw:md:!flex tw:!justify-between tw:!items-start">
                        <div className="tw:!w-3/4 tw:!flex tw:!gap-4">
                            <div className="tw:!w-1/3">
                                <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.pickupTime')}</p>
                                <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{moment(bookingDetail?.pickupDateTime).format(formatDate[i18n.language])}</p>
                            </div>
                            <div className="tw:!w-2/3">
                                <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.pickupLocation')}</p>
                                <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{getLocationDetail(bookingDetail?.pickupLocation).name}</p>
                                <p className="tw:!text-[13px] tw:!font-[400] tw:!text-[#7C7B7B] tw:!m-0">{getLocationDetail(bookingDetail?.pickupLocation).address}</p>
                            </div>
                        </div>
                    </div>

                    <hr className="tw:!my-4 tw:!border-gray-100 tw:!hidden tw:md:!block" />

                    {/* Desktop - Drop-off Section (Hidden on Mobile) */}
                    <div className="tw:!hidden tw:md:!flex tw:!justify-between tw:!items-start">
                        <div className="tw:!w-3/4 tw:!flex tw:!gap-4">
                            <div className="tw:!w-1/3">
                                <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t('myPage.booking.bookingItem.title.dropoffTime')}</p>
                                <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{moment(bookingDetail?.customerDropoffDateTime).format(formatDate[i18n.language])}</p>
                            </div>
                            <div className="tw:!w-2/3">
                                {bookingDetail?.destinations.map((item, index) => {
                                    const isSingle = bookingDetail.destinations.length === 1;
                                    const isLast = index === bookingDetail.destinations.length - 1;

                                    return (
                                        <div
                                            key={index}
                                            className={`tw:!w-full ${!isLast ? 'tw:!mb-6' : ''}`}
                                        >
                                            <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">
                                                {`${!isSingle ? `${index + 1}.` : ''} ${t('myPage.booking.bookingItem.title.dropoffLocation')}`}
                                            </p>

                                            <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">
                                                {getLocationDetail(item).name}
                                            </p>

                                            <p className="tw:!text-[13px] tw:!font-[400] tw:!text-[#7C7B7B] tw:!m-0">
                                                {getLocationDetail(item).address}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {
                        bookingDetail.bookingType === "byTheHour" && (
                            <>
                                <hr className="tw:!my-4 tw:!border-gray-100 tw:!hidden tw:md:!block" />

                                {/* Desktop - Travel Area Section (Hidden on Mobile) */}
                                <div className="tw:!hidden tw:md:!flex tw:!justify-between tw:!items-start">
                                    <div className="tw:!w-3/4 tw:!flex tw:!gap-3">
                                        <div className="tw:!w-1/3">
                                            <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.travelArea")}</p>
                                            <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{`${t("NewCart.AddOnServices.TravelArea." + bookingDetail.travelArea)}`}</p>
                                        </div>
                                        <div className="tw:!w-1/3 tw:!flex tw:!gap-2">
                                            <div className="tw:!w-1/2">
                                                <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.usageDuration")}</p>
                                                <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{`${minutesToHoursCeil(bookingDetail?.usageDuration)}${t("myPage.booking.bookingItem.title.hours")}`}</p>
                                            </div>
                                            <div className="tw:!w-1/2">
                                                <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.billedDuration")}</p>
                                                <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{`${minutesToHoursCeil(bookingDetail?.billedDuration)}${t("myPage.booking.bookingItem.title.hours")}`}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )
                    }

                    {/* Show More Section - Collapsible */}
                    <div
                        className={`tw:!overflow-hidden tw:!transition-all tw:!duration-500 tw:!ease-in-out ${showMore ? 'tw:!max-h-[500px] tw:!opacity-100' : 'tw:!max-h-0 tw:!opacity-0'}`}
                    >
                        <hr className="tw:!my-4 tw:!border-gray-100 tw:md:!hidden" />

                        {/* Row 5: Vehicle (Mobile) */}
                        {bookingDetail?.bookingInfo.map((item, index) => {
                            const isSingle = bookingDetail.bookingInfo.length === 1;
                            const isLast = index === bookingDetail.bookingInfo.length - 1;
                            return (
                                <div>
                                    <div
                                        key={index}
                                        className={`tw:md:!hidden ${!isLast ? 'tw:!mb-6' : ''}`}
                                    >
                                        <div className="tw:md:!hidden tw:!mb-2">
                                            <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{`${t("myPage.booking.bookingItem.title.vehicle")}${!isSingle ? ` ${index + 1}` : ''}`}</p>
                                            <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{t(item.vehicleTypeName.replace(/ /g, "."))}</p>
                                        </div>


                                        {/* Row 6: Passenger - Luggage (Mobile) */}
                                        <div className="tw:!flex tw:!justify-between tw:!items-start tw:!gap-4 tw:md:!hidden">
                                            <div className="tw:!flex-1">
                                                <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.passenger")}</p>
                                                <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">
                                                    {item.pax
                                                        .filter((passenger) => passenger.paxcount > 0)
                                                        .map((passenger, index, array) => (
                                                            <span key={passenger.paxtype}>
                                                                {t(`my_page.bookings_page.booking_item.${passenger.paxtype}`)}: {passenger.paxcount}
                                                                {index !== array.length - 1 ? ", " : ""}
                                                            </span>
                                                        ))}
                                                </p>
                                            </div>
                                            <div className="tw:!flex-1">
                                                <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.luggage")}</p>
                                                <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">
                                                    {item.luggage.filter((luggage) => luggage.luggagecount > 0).length > 0
                                                        ? item.luggage
                                                            .filter((luggage) => luggage.luggagecount > 0)
                                                            .map((luggage, index, array) => (
                                                                <span key={luggage.luggagetype}>
                                                                    {luggage.luggagetype === "CHECK-IN SIZE"
                                                                        ? t("my_page.bookings_page.booking_item.check_in")
                                                                        : t("my_page.bookings_page.booking_item.cabin")}
                                                                    : {luggage.luggagecount}
                                                                    {index !== array.length - 1 ? ", " : ""}
                                                                </span>
                                                            ))
                                                        : i18n.language === "en" ? <span className="tw:!text-[#8d8d8d] tw:!italic">None</span> : <span className="tw:!text-[#8d8d8d] tw:!italic">なし</span>}
                                                </p>
                                            </div>
                                        </div>
                                        {(index + 1) < bookingDetail.bookingInfo.length &&
                                            <hr className="tw:!my-4 tw:!border-gray-100 tw:md:!hidden" />
                                        }
                                    </div>
                                </div>
                            )
                        })}

                        <hr className="tw:!my-4 tw:!border-gray-100 tw:md:!hidden" />

                        {/* Row 7: Add on services (Mobile) */}
                        <div className="tw:md:!hidden">
                            <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.addOnService")}</p>
                            <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{getAddOnServices()}</p>
                        </div>

                        <hr className="tw:!my-4 tw:!border-gray-100 tw:md:!hidden" />

                        {/* Row 8: Additional Information (Mobile) */}
                        <div className="tw:md:!hidden">
                            <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.additionalInformation")}</p>
                            <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{getAdditionalInformation()}</p>
                        </div>

                        {/* Desktop - Show More Section */}
                        <hr className="tw:!my-4 tw:!border-gray-100 tw:!hidden tw:md:!block" />

                        {[...bookingDetail?.bookingInfo].map((item, index) => {
                            // const isSingle = bookingDetail.destinations.length === 1;
                            const isSingle = bookingDetail.bookingInfo.length === 1;
                            const isLast = index === bookingDetail.destinations.length - 1;
                            return (
                                <div>
                                    <div className={`tw:!hidden tw:md:!flex tw:!justify-between tw:!items-start ${!isLast ? 'tw:!mb-6' : ''}`}>
                                        <div className="tw:!w-3/4 tw:!flex tw:!gap-3">
                                            <div className="tw:!w-1/3">
                                                <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{`${t("myPage.booking.bookingItem.title.vehicle")}${!isSingle ? ` ${index + 1}` : ''}`}</p>
                                                <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{t(item.vehicleTypeName.replace(/ /g, "."))}</p>
                                            </div>
                                            <div className="tw:!w-1/2">
                                                <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.luggage")}</p>
                                                <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">
                                                    {item.luggage.filter((luggage) => luggage.luggagecount > 0).length > 0
                                                        ? item.luggage
                                                            .filter((luggage) => luggage.luggagecount > 0)
                                                            .map((luggage, index, array) => (
                                                                <span key={luggage.luggagetype}>
                                                                    {luggage.luggagetype === "CHECK-IN SIZE"
                                                                        ? t("my_page.bookings_page.booking_item.check_in")
                                                                        : t("my_page.bookings_page.booking_item.cabin")}
                                                                    : {luggage.luggagecount}
                                                                    {index !== array.length - 1 ? ", " : ""}
                                                                </span>
                                                            ))
                                                        : i18n.language === "en" ? <span className="tw:!text-[#8d8d8d] tw:!italic">None</span> : <span className="tw:!text-[#8d8d8d] tw:!italic">なし</span>}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    {(index + 1) < bookingDetail.bookingInfo.length &&
                                        <hr className="tw:!my-4 tw:!border-gray-100 tw:!hidden tw:md:!block" />
                                    }
                                </div>
                            )
                        })}

                        <hr className="tw:!my-4 tw:!border-gray-100 tw:!hidden tw:md:!block" />

                        <div className="tw:!hidden tw:md:!flex tw:!justify-between tw:!items-start">
                            <div className="tw:!w-3/4 tw:!flex tw:!gap-4">
                                <div className="tw:!w-1/3">
                                    <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.addOnService")}</p>
                                    <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{getAddOnServices()}</p>
                                </div>
                                <div className="tw:!w-2/3">
                                    <p className="tw:!text-[13px] tw:!text-[#A7A7A7] tw:!font-[400] tw:!mb-1">{t("myPage.booking.bookingItem.title.additionalInformation")}</p>
                                    <p className="tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!m-0">{getAdditionalInformation()}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Row 9: Show More/Less Button */}
                    {source !== "cart" && (
                        <div className="tw:!mt-6 tw:!border-t tw:!border-[#E8E8E8] tw:!-mx-4 tw:md:!mx-0 tw:md:!border-t-0">
                            {
                                !showMore ? (
                                    <button
                                        onClick={handleShowMoreClick}
                                        onMouseEnter={() => setIsHoveringMore(true)}
                                        onMouseLeave={() => setIsHoveringMore(false)}
                                        className="tw:!w-full tw:md:!w-auto tw:!flex tw:md:!inline-flex tw:!items-center tw:!justify-center tw:!gap-1 tw:!text-[14px] tw:!font-[400] tw:!text-[#8D8D8D] tw:!cursor-pointer tw:!transition-all tw:!duration-200 tw:hover:!text-[#282828] tw:hover:!font-[700] tw:!bg-transparent tw:!border-none tw:!pt-3 tw:md:!pt-0 tw:!pb-0 tw:!px-0"
                                    >
                                        <span className="tw:!inline-flex tw:!items-center tw:!leading-[14px]">
                                            {t('myPage.booking.bookingItem.showMore')}
                                        </span>
                                        <ShowMore
                                            color={isHoveringMore ? "#282828" : "#A7A7A7"}
                                            size={14}
                                        />
                                    </button>

                                ) : (
                                    <button
                                        onClick={handleShowLessClick}
                                        onMouseEnter={() => setIsHoveringLess(true)}
                                        onMouseLeave={() => setIsHoveringLess(false)}
                                        className="tw:!w-full tw:md:!w-auto tw:!flex tw:md:!inline-flex tw:!items-center tw:!justify-center tw:!gap-1 tw:!text-[14px] tw:!font-[400] tw:!text-[#8D8D8D] tw:!cursor-pointer tw:!transition-all tw:!duration-200 tw:hover:!text-[#282828] tw:hover:!font-[700] tw:!bg-transparent tw:!border-none tw:!pt-3 tw:md:!pt-0 tw:!pb-0 tw:!px-0"
                                    >
                                        <span className="tw:!inline-flex tw:!items-center tw:!leading-[14px]">
                                            {t('myPage.booking.bookingItem.showLess')}
                                        </span>
                                        <ShowLess color={isHoveringLess ? "#282828" : "#A7A7A7"} size={14} />
                                    </button>
                                )
                            }
                        </div>
                    )}
                </div>

                <div className="tw:!hidden tw:md:!flex tw:!bg-[#F9F9F9] tw:!px-4 tw:md:!px-8 tw:!py-4 tw:!border-t tw:!border-[#E8E8E8] tw:!justify-between tw:!items-center tw:!rounded-b-lg">
                    <div>
                        <p className="tw:!text-[15px] tw:!text-[#282828] tw:!font-[700] tw:!m-0">
                            {t('myPage.booking.bookingItem.title.price')}
                        </p>
                    </div>

                    <div className="tw:!text-right">
                        {
                            priceBreakdown.hasDiscount && (
                                <p className="tw:!text-[14px] tw:!text-[#E53935] tw:!font-[400] tw:!m-0 tw:!line-through">
                                    {`${priceBreakdown.priceBeforeDiscount.toLocaleString()}${t('myPage.booking.bookingItem.JPY')}`}
                                </p>
                            )
                        }
                        <p className="tw:!text-[15px] tw:!text-[#282828] tw:!font-[400] tw:!m-0">
                            {`${priceBreakdown.totalPrice.toLocaleString()}${t('myPage.booking.bookingItem.JPY')}`}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingCard;